import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HeartIcon, AwardIcon, StarIcon, DollarSignIcon, BuildingIcon, UsersIcon, CheckCircleIcon, ClipboardCheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const DonorRecognitionPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const topIndividualDonors = [{
    name: 'Dr. Eleanor Chen',
    tier: 'Visionary',
    years: '2021-Present',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "The Ukweli Institute's commitment to scientific integrity in the wellness space inspired me to support their mission. Their work is transforming how we approach health and wellness products."
  }, {
    name: 'Michael & Sarah Rodriguez',
    tier: 'Champion',
    years: '2020-Present',
    image: 'https://images.unsplash.com/photo-1531747118685-ca8fa6e08806?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: 'We believe that everyone deserves access to evidence-based information about products that affect their health. The Ukweli Institute is making that possible.'
  }, {
    name: 'James Wilson',
    tier: 'Visionary',
    years: '2022-Present',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "As someone who has seen the harm that can come from unsubstantiated health claims, I'm proud to support an organization that upholds scientific integrity."
  }, {
    name: 'Dr. Amara Patel',
    tier: 'Champion',
    years: '2021-Present',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: 'The Ukweli Institute represents the gold standard in microbiome research and consumer education. Their work is essential for advancing this field.'
  }, {
    name: 'Robert & Linda Thompson',
    tier: 'Advocate',
    years: '2020-Present',
    image: 'https://images.unsplash.com/photo-1515463892140-58a22e37ff72?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "We've been supporting the Ukweli Institute since its founding because we believe in the power of science to improve lives."
  }, {
    name: 'Thomas Lee',
    tier: 'Advocate',
    years: '2022-Present',
    image: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    quote: "The transparency and rigor that the Ukweli Institute brings to wellness research is unmatched. I'm honored to support their mission."
  }];
  const corporatePartners = [{
    name: 'HealthTech Innovations',
    tier: 'Platinum Partner',
    years: '2020-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Supporting research on digital health technologies and microbiome science'
  }, {
    name: 'NutriWell Labs',
    tier: 'Gold Partner',
    years: '2021-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Funding research on supplement efficacy and safety standards'
  }, {
    name: 'BioPure Solutions',
    tier: 'Gold Partner',
    years: '2022-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Supporting educational initiatives on microbiome health'
  }, {
    name: 'Global Wellness Foundation',
    tier: 'Silver Partner',
    years: '2021-Present',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    description: 'Funding research on international wellness standards'
  }];
  const donorImpact = [{
    title: 'Research Advancement',
    description: 'Donor support has enabled 15 new research studies on microbiome science and wellness product efficacy in the past year alone.',
    icon: <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-696" data-mp="fbai0add9c2e" />
  }, {
    title: 'Educational Resources',
    description: "We've developed 30+ evidence-based educational resources that have reached over 50,000 consumers and healthcare providers.",
    icon: <UsersIcon size={24} className="text-blue-600" data-id="element-697" data-mp="n65jo4wpws2" />
  }, {
    title: 'Certification Standards',
    description: 'Our donor-funded certification program has evaluated over 200 products, helping consumers identify truly effective options.',
    icon: <CheckCircleIcon size={24} className="text-blue-600" data-id="element-698" data-mp="3nfl3i8mon7hf" />
  }, {
    title: 'Global Outreach',
    description: 'Donor support has helped us expand our research and educational initiatives to 5 new countries in the past year.',
    icon: <BuildingIcon size={24} className="text-blue-600" data-id="element-699" data-mp="3dn5ptnif552c" />
  }];
  return <div className="bg-white w-full" data-id="element-700" data-mp="59zr8w8imwub">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-701" data-mp="a8t6ou7d4xx4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-702" data-mp="a66z4ukh2iew">
          <div className="text-center" data-id="element-703" data-mp="3rg53djyn4p79">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-704" data-mp="adfkr8c5onm">
              Donor Recognition
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-705" data-mp="2g188vujot7si">
              Honoring those who make our mission possible through their
              generous support
            </p>
          </div>
        </div>
      </section>
      {/* Introduction Section */}
      <section className="py-16 bg-white" data-id="element-706" data-mp="mj0z3us8s8t4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-707" data-mp="mk3ofy9x236x">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-708" data-mp="hriwz3ehg26k">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-709" data-mp="3sj9w06mbvci">
              Thank You to Our Supporters
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-710" data-mp="3uznekofaj5hr">
              The Ukweli Institute's work is made possible through the generous
              support of individuals, foundations, and corporate partners who
              share our commitment to advancing evidence-based science in the
              wellness industry. We are deeply grateful for their contributions.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Individual Donors Section */}
      <section className="py-16 bg-gray-50" data-id="element-711" data-mp="20pyqu9mzlpg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-712" data-mp="20oxbhsyxugsj">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-713" data-mp="25ii415hriikc">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-714" data-mp="1mqplk50bkfqv">
              <HeartIcon size={28} data-id="element-715" data-mp="1rqtvfc6q2c84" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-716" data-mp="2y6e8iwdkpero">
              Individual Donors
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-717" data-mp="334frziewvg3o">
              We recognize the following individuals for their exceptional
              commitment to our mission through their generous financial
              support.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-718" data-mp="1touki01793l">
            {topIndividualDonors.map((donor, index) => <motion.div key={donor.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-719" data-mp="3t7kr4m4xn76b" data-mpi={index}>
                <div className="p-6" data-id="element-720" data-mp="2hkt4wjeg9p21">
                  <div className="flex items-center mb-4" data-id="element-721" data-mp="2hlut7pkh881p">
                    <img src={donor.image} alt={donor.name} className="h-16 w-16 rounded-full object-cover mr-4" onError={e => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=250&q=80';
                }} data-id="element-722" data-mp="x2sbcda3845i" />
                    <div data-id="element-723" data-mp="ebhp49fb4fjy">
                      <h3 className="text-xl font-bold text-gray-900" data-id="element-724" data-mp="eednfyv2h68x">
                        {donor.name}
                      </h3>
                      <div className="flex items-center" data-id="element-725" data-mp="4ed081u2u5zg">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2" data-id="element-726" data-mp="4fxtlo4nnivq">
                          {donor.tier}
                        </span>
                        <span className="text-sm text-gray-600" data-id="element-727" data-mp="1yqbbe9by5bxz">
                          {donor.years}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 italic" data-id="element-728" data-mp="3byqzmtzvizwv">"{donor.quote}"</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Corporate Partners Section */}
      <section className="py-16 bg-white" data-id="element-729" data-mp="328ei7ancvmuy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-730" data-mp="3297f7jwiixzw">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-731" data-mp="3py8t3mo471ck">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-732" data-mp="24knxca5ebjgt">
              <BuildingIcon size={28} data-id="element-733" data-mp="29lbnyupppyai" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-734" data-mp="rmnbny80rhiu">
              Corporate & Foundation Partners
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-735" data-mp="moc63zorbt6f">
              We are grateful to the following organizations for their
              significant support of our research and educational initiatives.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-736" data-mp="wjvy4b07a7pw">
            {corporatePartners.map((partner, index) => <motion.div key={partner.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-gray-50 rounded-lg p-6 shadow-sm" data-id="element-737" data-mp="3xkx4z8om4sk" data-mpi={index}>
                <div className="flex flex-col md:flex-row items-center" data-id="element-738" data-mp="1kas532ztivpn">
                  <div className="flex-shrink-0 w-24 h-24 bg-white rounded-lg flex items-center justify-center mb-4 md:mb-0 md:mr-6" data-id="element-739" data-mp="1p9cwqe10872h">
                    <img src={partner.logo} alt={partner.name} className="max-h-16 max-w-full" onError={e => {
                  e.currentTarget.src = 'https://via.placeholder.com/150/e2e8f0/1e40af?text=Logo';
                }} data-id="element-740" data-mp="1u1wihy9ui8id" />
                  </div>
                  <div data-id="element-741" data-mp="1bcpz09189cvi">
                    <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-742" data-mp="1g9q68n9tclo7">
                      {partner.name}
                    </h3>
                    <div className="flex flex-wrap items-center mb-2" data-id="element-743" data-mp="2moqsg191dphl">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2" data-id="element-744" data-mp="2romwu1lnbixo">
                        {partner.tier}
                      </span>
                      <span className="text-sm text-gray-600" data-id="element-745" data-mp="b1x3oo4qpm0r">
                        {partner.years}
                      </span>
                    </div>
                    <p className="text-gray-700" data-id="element-746" data-mp="69ndd6zpqjxy">{partner.description}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Impact Section */}
      <section className="py-16 bg-gray-50" data-id="element-747" data-mp="3ohlb36iujn2v">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-748" data-mp="3ofs1bicxsmqn">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-749" data-mp="3jhgqb4awo66u">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-750" data-mp="aenudkeiagt7">
              <StarIcon size={28} data-id="element-751" data-mp="eeya3os3oll9" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-752" data-mp="edn21crmgroc">
              Donor Impact
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-753" data-mp="5f1e123dskg6">
              Our donors' generous support has enabled significant progress in
              our mission to advance evidence-based science in the wellness
              industry.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-754" data-mp="3rocgt9hs9mqy">
            {donorImpact.map((impact, index) => <motion.div key={impact.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-755" data-mp="3itxz709s604o" data-mpi={index}>
                <div className="flex items-start" data-id="element-756" data-mp="2z97cq2ezf3hj">
                  <div className="flex-shrink-0 mr-4" data-id="element-757" data-mp="2u9kwrl7rr951">
                    <div className="bg-blue-50 rounded-full p-3" data-id="element-758" data-mp="2u7qd0a88hn2j">
                      {impact.icon}
                    </div>
                  </div>
                  <div data-id="element-759" data-mp="3480onqememfl">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-760" data-mp="346fw26bv9dkd">
                      {impact.title}
                    </h3>
                    <p className="text-gray-700" data-id="element-761" data-mp="3i1011jj3w0ix">{impact.description}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Legacy Giving Section */}
      <section className="py-16 bg-white" data-id="element-762" data-mp="3myienn82j3x9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-763" data-mp="3mzjqvv0tlwgk">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-764" data-mp="3u1ycj5mnc8x2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-765" data-mp="hk1qsjw8op36">
              <AwardIcon size={28} data-id="element-766" data-mp="lkc0a6j8pxms" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-767" data-mp="qkghjyrz2we2">
              Legacy Society
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-768" data-mp="vjawgpmtk0sw">
              We honor those who have included the Ukweli Institute in their
              estate plans, ensuring our work continues for generations to come.
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-gray-50 rounded-lg p-8 shadow-md" data-id="element-769" data-mp="2xb63ut21lj5b">
            <div className="text-center mb-8" data-id="element-770" data-mp="2b0e9enltkzq1">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-771" data-mp="38qixwlciznnc">
                The Ukweli Legacy Society Members
              </h3>
              <p className="text-gray-700" data-id="element-772" data-mp="33rxwwjpg8zxu">
                We gratefully acknowledge the following individuals who have
                made provisions for the Ukweli Institute in their estate plans:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-center" data-id="element-773" data-mp="1ncwl521rkjrt">
              <div className="p-4" data-id="element-774" data-mp="1idtjlrkzsvol">
                <p className="font-medium text-gray-900" data-id="element-775" data-mp="1ics5fe8ojt2n">
                  Dr. Margaret Williams
                </p>
              </div>
              <div className="p-4" data-id="element-776" data-mp="2841xpxicndip">
                <p className="font-medium text-gray-900" data-id="element-777" data-mp="2d0s9i8n4fmyb">
                  John & Patricia Anderson
                </p>
              </div>
              <div className="p-4" data-id="element-778" data-mp="1z2vdbdckxik6">
                <p className="font-medium text-gray-900" data-id="element-779" data-mp="1z3n6yimm967t">Richard Lee, PhD</p>
              </div>
              <div className="p-4" data-id="element-780" data-mp="234pahbnkg4fl">
                <p className="font-medium text-gray-900" data-id="element-781" data-mp="2qs6ugl0l9y80">Elizabeth Chen</p>
              </div>
              <div className="p-4" data-id="element-782" data-mp="2vs33iyo58fuf">
                <p className="font-medium text-gray-900" data-id="element-783" data-mp="2vr1qwprhblal">
                  David & Susan Thompson
                </p>
              </div>
              <div className="p-4" data-id="element-784" data-mp="2m1qgmzsuwal4">
                <p className="font-medium text-gray-900" data-id="element-785" data-mp="2m0ouo0aay3om">Anonymous (3)</p>
              </div>
            </div>
            <div className="mt-8 text-center" data-id="element-786" data-mp="39qj7ihumfs8v">
              <p className="text-gray-700 mb-4" data-id="element-787" data-mp="3ei164z969gqo">
                To learn more about including the Ukweli Institute in your
                estate plans, please contact our Development Office.
              </p>
              <Link to="/contact" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 transition-colors duration-200" data-id="element-788" data-mp="y48w08515ozq">
                Contact Us About Legacy Giving
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-blue-50" data-id="element-789" data-mp="f5d453gn9mp2">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-790" data-mp="f6epq1vadrl1">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-791" data-mp="u2x601i8v1r0">
            <h2 className="text-3xl font-bold text-white mb-6" data-id="element-792" data-mp="2z7pffkzsb4qn">
              Join Our Community of Supporters
            </h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-793" data-mp="2u95vfq4r430x">
              Your support can help advance evidence-based science and empower
              consumers with trustworthy information. Consider making a donation
              today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-794" data-mp="3i4i8ph8yc5kz">
              <Link to="/support-us" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center" data-id="element-795" data-mp="3d6p6t0eolxqi">
                <DollarSignIcon size={18} className="mr-2" data-id="element-796" data-mp="3mws5nh8hi5wr" />
                Make a Donation
              </Link>
              <Link to="/contact" className="px-6 py-3 bg-blue-700 text-white font-medium rounded-md shadow-md hover:bg-blue-800 transition-colors duration-300 flex items-center justify-center" data-id="element-797" data-mp="4qx4kkl0du5j">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};